<template>
  <div class="animated fadeIn configuration">
    <div class="card shadow-sm border-0 mb-3">
      <div class="card-header bg-facebook text-white py-3">
        <h5 class="mb-0 font-weight-bold"><font-awesome-icon icon="cogs" /> Configuration</h5>
        <small class="text-white-50">Reference lists and settings used across the system, grouped by category.</small>
      </div>
      <div class="card-body py-2">
        <b-input-group size="sm">
          <b-input-group-prepend>
            <b-input-group-text class="bg-dark text-white"><font-awesome-icon icon="search" /></b-input-group-text>
          </b-input-group-prepend>
          <b-form-input v-model="search" placeholder="Find a setting..." autocomplete="off"></b-form-input>
          <b-input-group-append>
            <b-button variant="dark" size="sm" @click="search = ''" :disabled="search === ''"><font-awesome-icon icon="times" /></b-button>
          </b-input-group-append>
        </b-input-group>
      </div>
    </div>

    <div v-for="category in visibleCategories" :key="category.name" class="mb-4">
      <h6 class="text-uppercase text-muted font-weight-bold small mb-2 pl-1">
        <font-awesome-icon :icon="category.icon" class="mr-1" /> {{ category.name }}
      </h6>
      <b-row>
        <b-col v-for="item in category.items" :key="item.to" sm="6" lg="4" xl="3" class="mb-3">
          <router-link :to="item.to" class="config-tile card shadow-sm border-0 h-100 text-decoration-none">
            <div class="card-body d-flex align-items-start py-3">
              <div class="config-icon rounded d-flex align-items-center justify-content-center mr-3" :class="category.color">
                <font-awesome-icon :icon="item.icon" />
              </div>
              <div>
                <div class="font-weight-bold text-dark">{{ item.name }}</div>
                <small class="text-muted">{{ item.description }}</small>
              </div>
            </div>
          </router-link>
        </b-col>
      </b-row>
    </div>

    <div v-if="visibleCategories.length === 0" class="text-center text-muted py-5">
      No setting matches "{{ search }}".
    </div>
  </div>
</template>

<script>
  export default {
    name: 'Configuration',
    data() {
      return {
        search: '',
        categories: [
          {
            name: 'Materials & Supply',
            icon: 'cubes',
            color: 'bg-info',
            items: [
              { name: 'Materials', to: '/maintenance/materials', icon: 'cubes', description: 'Master list of construction materials.' },
              { name: 'Material Units', to: '/maintenance/material-units', icon: 'cube', description: 'Units of measure for materials (bag, pc, m³...).' },
              { name: 'Item Units', to: '/maintenance/item-units', icon: 'sort', description: 'Units of measure for project structure items.' },
              { name: 'Suppliers', to: '/maintenance/suppliers', icon: 'truck', description: 'Suppliers and the materials they provide.' },
            ]
          },
          {
            name: 'Equipment & Tools',
            icon: 'cogs',
            color: 'bg-secondary',
            items: [
              { name: 'Equipments', to: '/maintenance/equipments', icon: 'cogs', description: 'Heavy equipment and their types.' },
              { name: 'Tools', to: '/maintenance/tools', icon: 'wrench', description: 'Hand tools and small equipment.' },
              { name: 'Maintenance Descriptions', to: '/maintenance/maintenance-descriptions', icon: 'tasks', description: 'Descriptions used when logging equipment maintenance.' },
            ]
          },
          {
            name: 'Employees & Pay',
            icon: 'users',
            color: 'bg-success',
            items: [
              { name: 'Job Titles', to: '/maintenance/job-descriptions', icon: 'user', description: 'Job titles with monthly and daily rates.' },
              { name: 'OT/ND Table', to: '/maintenance/computation-table', icon: 'chart-line', description: 'Overtime and night-differential computation table.' },
              { name: 'Other Expense Descriptions', to: '/maintenance/other-expenses', icon: 'tag', description: 'Categories for other project expenses.' },
            ]
          },
          {
            name: 'Time Log',
            icon: 'clock',
            color: 'bg-dark',
            roles: [1, 2],
            items: [
              { name: 'Payroll Groups & Shifts', to: '/maintenance/payroll-groups', icon: 'users', description: 'Payroll groups, their shifts and schedules: duty hours, grace, early scan, camera, OT premium.', roles: [1, 2] },
              { name: 'Kiosk Devices', to: '/maintenance/office-kiosk', icon: 'qrcode', description: 'Activation codes and the machines allowed to run the kiosk.', roles: [1, 2] },
              { name: 'Holidays', to: '/maintenance/holidays', icon: 'calendar', description: 'Regular and special holidays used for pay.', roles: [1, 2] },
              { name: 'Scheduled OT Permission', to: '/maintenance/ot-schedules', icon: 'sign-out-alt', description: 'Dates, time windows and purpose when overtime may be punched on the kiosk.', roles: [1, 2] },
            ]
          },
        ]
      }
    },
    computed: {
      roleId() {
        const user = this.$store.getters.userDetails;
        return user ? Number(user.role_id) : null;
      },
      visibleCategories() {
        const allowed = (entry) => !Array.isArray(entry.roles) || entry.roles.indexOf(this.roleId) !== -1;
        const term = this.search.trim().toLowerCase();
        return this.categories
          .filter(allowed)
          .map(category => Object.assign({}, category, {
            items: category.items.filter(allowed).filter(item =>
              term === '' || (item.name + ' ' + item.description).toLowerCase().indexOf(term) !== -1
            )
          }))
          .filter(category => category.items.length > 0);
      }
    }
  }
</script>

<style scoped>
  .config-tile {
    transition: transform 0.12s ease, box-shadow 0.12s ease;
  }
  .config-tile:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12) !important;
  }
  .config-icon {
    width: 40px;
    height: 40px;
    flex: 0 0 40px;
    color: #fff;
    font-size: 1.1rem;
  }
</style>
